import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import {
  FileText, Plus, DollarSign, Clock, Users, TrendingUp,
  Send, Check, AlertTriangle, Trash2, Edit, ArrowRight,
  Play, Square, Timer, BarChart3, CreditCard, Briefcase,
  Loader2, ChevronDown
} from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-neutral-100 text-neutral-700",
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  overdue: "bg-red-100 text-red-700",
  pending: "bg-amber-100 text-amber-700",
  converted: "bg-purple-100 text-purple-700",
  active: "bg-green-100 text-green-700",
  completed: "bg-neutral-100 text-neutral-700",
};

export function BillingDashboard({ open, onOpenChange }) {
  const [activeTab, setActiveTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [reports, setReports] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [timeData, setTimeData] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createType, setCreateType] = useState("invoice");
  const [form, setForm] = useState({});
  const [lineItems, setLineItems] = useState([{ description: "", quantity: 1, rate: 0 }]);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, estRes, ctrRes, repRes, payRes, timeRes] = await Promise.all([
        authFetch(`${API}/billing/invoices`),
        authFetch(`${API}/billing/estimates`),
        authFetch(`${API}/billing/contracts`),
        authFetch(`${API}/billing/reports`),
        authFetch(`${API}/billing/payouts`),
        authFetch(`${API}/billing/time-entries`),
      ]);
      if (invRes.ok) { const d = await invRes.json(); setInvoices(d.invoices || []); setStats(d.stats || {}); }
      if (estRes.ok) { const d = await estRes.json(); setEstimates(d.estimates || []); }
      if (ctrRes.ok) { const d = await ctrRes.json(); setContracts(d.contracts || []); }
      if (repRes.ok) setReports(await repRes.json());
      if (payRes.ok) setPayouts(await payRes.json());
      if (timeRes.ok) setTimeData(await timeRes.json());
    } catch (e) { console.error("Billing fetch error:", e); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  const openCreate = (type) => {
    setCreateType(type);
    setForm({});
    setLineItems([{ description: "", quantity: 1, rate: 0 }]);
    setShowCreateDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = createType === "invoice" ? "invoices" : createType === "estimate" ? "estimates" : "contracts";
      const editingId = form._editing_id;
      const { _editing_id, ...cleanForm } = form;
      const body = createType === "contract"
        ? { ...cleanForm }
        : { ...cleanForm, line_items: lineItems.filter(i => i.description) };
      
      const url = editingId ? `${API}/billing/${endpoint}/${editingId}` : `${API}/billing/${endpoint}`;
      const method = editingId ? "PUT" : "POST";
      const res = await authFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) { toast.success(editingId ? `${createType} updated` : `${createType} created`); setShowCreateDialog(false); fetchData(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed to save"); }
    } catch (e) { toast.error("Failed to save"); }
    setSaving(false);
  };

  const updateStatus = async (type, id, status) => {
    const endpoint = type === "invoice" ? `invoices/${id}` : `contracts/${id}`;
    const res = await authFetch(`${API}/billing/${endpoint}`, { method: "PUT", body: JSON.stringify({ status }) });
    if (res.ok) { toast.success("Status updated"); fetchData(); }
  };

  const deleteItem = async (type, id) => {
    const endpoint = type === "invoice" ? `invoices/${id}` : type === "estimate" ? `estimates/${id}` : `contracts/${id}`;
    const res = await authFetch(`${API}/billing/${endpoint}`, { method: "DELETE" });
    if (res.ok) { toast.success("Deleted"); fetchData(); }
  };

  const convertEstimate = async (estimateId) => {
    const res = await authFetch(`${API}/billing/estimates/${estimateId}/convert`, { method: "POST" });
    if (res.ok) { const d = await res.json(); toast.success(`Converted to ${d.invoice_id}`); fetchData(); }
  };

  const handleClockIn = async () => {
    const activity = prompt("What are you working on?");
    if (!activity) return;
    const res = await authFetch(`${API}/billing/time-entries/clock-in`, { method: "POST", body: JSON.stringify({ activity }) });
    if (res.ok) { toast.success("Clocked in"); fetchData(); }
    else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed"); }
  };

  const handleClockOut = async () => {
    const res = await authFetch(`${API}/billing/time-entries/clock-out`, { method: "POST", body: JSON.stringify({}) });
    if (res.ok) { const d = await res.json(); toast.success(`Clocked out — ${d.duration_minutes} min`); fetchData(); }
    else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed"); }
  };

  const recordPayout = async () => {
    const consultantId = prompt("Consultant user ID:");
    const amount = prompt("Payout amount ($):");
    if (!consultantId || !amount) return;
    const res = await authFetch(`${API}/billing/payouts`, {
      method: "POST", body: JSON.stringify({ consultant_id: consultantId, amount: parseFloat(amount), method: "bank_transfer" })
    });
    if (res.ok) { toast.success("Payout recorded"); fetchData(); }
  };

  const itemTotal = lineItems.reduce((s, i) => s + (i.quantity || 0) * (i.rate || 0), 0);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="billing-dashboard">
        <DialogHeader className="p-5 border-b">
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#2BC40E]" /> Billing & Invoicing
          </DialogTitle>
          <DialogDescription>Invoices, estimates, contracts, time tracking & payouts</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" /></div>
          ) : (
            <>
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {[
                  { label: "Total Invoiced", value: `$${(stats.total_amount || 0).toLocaleString()}`, icon: FileText, color: "text-neutral-700" },
                  { label: "Paid", value: `$${(stats.paid_amount || 0).toLocaleString()}`, icon: Check, color: "text-green-600" },
                  { label: "Outstanding", value: `$${(stats.outstanding || 0).toLocaleString()}`, icon: AlertTriangle, color: "text-amber-600" },
                  { label: "Invoices", value: stats.total || 0, icon: FileText, color: "text-blue-600" },
                  { label: "Today's Hours", value: `${timeData?.today_total_hours || 0}h`, icon: Clock, color: "text-purple-600" },
                ].map(s => (
                  <Card key={s.label} className="border-neutral-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <s.icon className={`w-4 h-4 ${s.color}`} />
                        <span className="text-[10px] text-neutral-500 uppercase">{s.label}</span>
                      </div>
                      <p className="text-xl font-bold text-neutral-900">{s.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Time Tracker Bar */}
              <Card className="border-neutral-200 mb-6 bg-neutral-50">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Timer className="w-5 h-5 text-[#2BC40E]" />
                    <div>
                      <p className="font-semibold text-sm text-neutral-900">Time Tracker</p>
                      <p className="text-xs text-neutral-500">
                        {timeData?.active_entry
                          ? `Clocked in — ${timeData.active_entry.activity || "Working"}`
                          : "Not clocked in"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-neutral-700">{timeData?.today_total_hours || 0}h today</span>
                    {timeData?.active_entry ? (
                      <Button size="sm" onClick={handleClockOut} className="bg-red-600 hover:bg-red-700 text-white" data-testid="clock-out-btn">
                        <Square className="w-3.5 h-3.5 mr-1" /> Clock Out
                      </Button>
                    ) : (
                      <Button size="sm" onClick={handleClockIn} className="bg-[#333333] hover:bg-[#444444] text-white" data-testid="clock-in-btn">
                        <Play className="w-3.5 h-3.5 mr-1" /> Clock In
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-5 mb-4">
                  <TabsTrigger value="invoices">Invoices</TabsTrigger>
                  <TabsTrigger value="estimates">Estimates</TabsTrigger>
                  <TabsTrigger value="contracts">Contracts</TabsTrigger>
                  <TabsTrigger value="reports">Reports</TabsTrigger>
                  <TabsTrigger value="payouts">Payouts</TabsTrigger>
                </TabsList>

                {/* ── INVOICES TAB ── */}
                <TabsContent value="invoices">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-neutral-900">Invoices ({invoices.length})</h3>
                    <Button size="sm" onClick={() => openCreate("invoice")} className="bg-[#333333] text-white" data-testid="create-invoice-btn">
                      <Plus className="w-4 h-4 mr-1" /> New Invoice
                    </Button>
                  </div>
                  {invoices.length === 0 ? (
                    <p className="text-neutral-500 text-center py-8">No invoices yet</p>
                  ) : (
                    <div className="space-y-2">
                      {invoices.map(inv => (
                        <Card key={inv.invoice_id} className="border-neutral-200" data-testid={`invoice-${inv.invoice_id}`}>
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-bold text-neutral-900">{inv.invoice_id}</span>
                                <Badge className={STATUS_COLORS[inv.status] || STATUS_COLORS.draft}>{inv.status}</Badge>
                              </div>
                              <p className="text-sm text-neutral-600 truncate">{inv.client_name || "No client"}</p>
                              <p className="text-xs text-neutral-400">{inv.line_items?.length || 0} items • Due: {inv.due_date || "N/A"}</p>
                            </div>
                            <p className="text-lg font-bold text-neutral-900">${inv.total?.toLocaleString()}</p>
                            <div className="flex gap-1">
                              {inv.status === "draft" && (
                                <>
                                <Button size="sm" variant="outline" className="text-blue-600" onClick={() => {
                                  setCreateType("invoice");
                                  setForm({ client_name: inv.client_name, client_email: inv.client_email, notes: inv.notes, tax_rate: inv.tax_rate, due_date: inv.due_date, _editing_id: inv.invoice_id });
                                  setLineItems(inv.line_items?.length > 0 ? inv.line_items : [{ description: "", quantity: 1, rate: 0 }]);
                                  setShowCreateDialog(true);
                                }}><Edit className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="outline" onClick={() => updateStatus("invoice", inv.invoice_id, "sent")}><Send className="w-3.5 h-3.5" /></Button>
                                </>
                              )}
                              {inv.status === "sent" && (
                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => updateStatus("invoice", inv.invoice_id, "paid")}><Check className="w-3.5 h-3.5" /></Button>
                              )}
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteItem("invoice", inv.invoice_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* ── ESTIMATES TAB ── */}
                <TabsContent value="estimates">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-neutral-900">Estimates ({estimates.length})</h3>
                    <Button size="sm" onClick={() => openCreate("estimate")} className="bg-[#333333] text-white">
                      <Plus className="w-4 h-4 mr-1" /> New Estimate
                    </Button>
                  </div>
                  {estimates.length === 0 ? (
                    <p className="text-neutral-500 text-center py-8">No estimates yet</p>
                  ) : (
                    <div className="space-y-2">
                      {estimates.map(est => (
                        <Card key={est.estimate_id} className="border-neutral-200">
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-bold">{est.estimate_id}</span>
                                <Badge className={STATUS_COLORS[est.status]}>{est.status}</Badge>
                              </div>
                              <p className="text-sm text-neutral-600">{est.client_name}</p>
                            </div>
                            <p className="text-lg font-bold">${est.total?.toLocaleString()}</p>
                            <div className="flex gap-1">
                              {est.status === "pending" && (
                                <>
                                <Button size="sm" variant="outline" className="text-blue-600" onClick={() => {
                                  setCreateType("estimate");
                                  setForm({ client_name: est.client_name, client_email: est.client_email, notes: est.notes, tax_rate: est.tax_rate, valid_until: est.valid_until, _editing_id: est.estimate_id });
                                  setLineItems(est.line_items?.length > 0 ? est.line_items : [{ description: "", quantity: 1, rate: 0 }]);
                                  setShowCreateDialog(true);
                                }}><Edit className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => convertEstimate(est.estimate_id)}>
                                  <ArrowRight className="w-3.5 h-3.5 mr-1" /> Convert
                                </Button>
                                </>
                              )}
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteItem("estimate", est.estimate_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* ── CONTRACTS TAB ── */}
                <TabsContent value="contracts">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-neutral-900">Contracts ({contracts.length})</h3>
                    <Button size="sm" onClick={() => openCreate("contract")} className="bg-[#333333] text-white">
                      <Plus className="w-4 h-4 mr-1" /> New Contract
                    </Button>
                  </div>
                  {contracts.length === 0 ? (
                    <p className="text-neutral-500 text-center py-8">No contracts yet</p>
                  ) : (
                    <div className="space-y-2">
                      {contracts.map(ctr => (
                        <Card key={ctr.contract_id} className="border-neutral-200">
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-bold">{ctr.contract_id}</span>
                                <Badge className={STATUS_COLORS[ctr.status]}>{ctr.status}</Badge>
                              </div>
                              <p className="text-sm font-medium text-neutral-900">{ctr.title}</p>
                              <p className="text-xs text-neutral-500">{ctr.client_name} • {ctr.service_type}</p>
                            </div>
                            <p className="text-lg font-bold">${(ctr.total_value || 0).toLocaleString()}</p>
                            <div className="flex gap-1">
                              {ctr.status === "draft" && (
                                <Button size="sm" variant="outline" onClick={() => updateStatus("contract", ctr.contract_id, "active")}><Check className="w-3.5 h-3.5 mr-1" /> Activate</Button>
                              )}
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteItem("contract", ctr.contract_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* ── REPORTS TAB ── */}
                <TabsContent value="reports">
                  {reports && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Card className="border-neutral-200"><CardContent className="p-4">
                          <p className="text-xs text-neutral-500 mb-1">Total Invoiced</p>
                          <p className="text-2xl font-bold">${reports.summary.total_invoiced.toLocaleString()}</p>
                        </CardContent></Card>
                        <Card className="border-neutral-200"><CardContent className="p-4">
                          <p className="text-xs text-neutral-500 mb-1">Total Paid</p>
                          <p className="text-2xl font-bold text-green-600">${reports.summary.total_paid.toLocaleString()}</p>
                        </CardContent></Card>
                        <Card className="border-neutral-200"><CardContent className="p-4">
                          <p className="text-xs text-neutral-500 mb-1">Outstanding</p>
                          <p className="text-2xl font-bold text-amber-600">${reports.summary.total_outstanding.toLocaleString()}</p>
                        </CardContent></Card>
                        <Card className="border-neutral-200"><CardContent className="p-4">
                          <p className="text-xs text-neutral-500 mb-1">Collection Rate</p>
                          <p className="text-2xl font-bold text-blue-600">{reports.summary.collection_rate}%</p>
                        </CardContent></Card>
                      </div>
                      {/* Aging Report */}
                      <Card className="border-neutral-200">
                        <CardHeader className="pb-2"><CardTitle className="text-sm">Aging Report</CardTitle></CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-4 gap-3 text-center">
                            {Object.entries(reports.aging).map(([k, v]) => (
                              <div key={k} className="p-3 bg-neutral-50 rounded-lg">
                                <p className="text-xs text-neutral-500">{k.replace("_", " ").replace("plus", "+")}</p>
                                <p className="text-lg font-bold">${v.toLocaleString()}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                      {/* Monthly Revenue */}
                      {Object.keys(reports.monthly_revenue).length > 0 && (
                        <Card className="border-neutral-200">
                          <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly Revenue</CardTitle></CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {Object.entries(reports.monthly_revenue).map(([month, amount]) => (
                                <div key={month} className="flex items-center justify-between p-2 bg-neutral-50 rounded">
                                  <span className="text-sm font-medium">{month}</span>
                                  <span className="font-bold text-green-600">${amount.toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* ── PAYOUTS TAB ── */}
                <TabsContent value="payouts">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-neutral-900">Consultant Payouts (70/30 Split)</h3>
                    <Button size="sm" onClick={recordPayout} className="bg-[#333333] text-white">
                      <DollarSign className="w-4 h-4 mr-1" /> Record Payout
                    </Button>
                  </div>
                  {payouts?.consultants && Object.keys(payouts.consultants).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(payouts.consultants).map(([cid, data]) => (
                        <Card key={cid} className="border-neutral-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-neutral-900">{data.name}</p>
                                <p className="text-xs text-neutral-500">{data.invoices} paid invoices</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-neutral-500">Earned: <span className="font-bold text-neutral-900">${data.total_earned.toLocaleString()}</span></p>
                                <p className="text-sm text-neutral-500">Paid Out: <span className="font-bold text-green-600">${data.total_paid_out.toLocaleString()}</span></p>
                                <p className="text-sm font-bold text-amber-600">Owed: ${data.balance_owed.toLocaleString()}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-neutral-500 text-center py-8">No paid invoices yet — payouts calculated from paid invoices with 70/30 split</p>
                  )}
                </TabsContent>
              </Tabs>

              {/* Time Entries Log */}
              {timeData?.entries?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#2BC40E]" /> Recent Time Entries
                  </h3>
                  <div className="space-y-1.5">
                    {timeData.entries.slice(0, 10).map(e => (
                      <div key={e.entry_id} className="flex items-center gap-3 p-2.5 bg-neutral-50 rounded-lg text-sm">
                        <span className="font-mono text-xs text-neutral-400 w-20">{e.date}</span>
                        <span className="flex-1 truncate text-neutral-700">{e.activity || "—"}</span>
                        <span className="text-xs text-neutral-500">{e.clock_in?.slice(11, 16)} → {e.clock_out?.slice(11, 16) || "..."}</span>
                        <Badge variant="outline" className="text-xs">{e.duration_minutes || 0} min</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New {createType.charAt(0).toUpperCase() + createType.slice(1)}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Client Name</Label><Input value={form.client_name || ""} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="Client name" /></div>
                <div><Label className="text-xs">Client Email</Label><Input value={form.client_email || ""} onChange={e => setForm({ ...form, client_email: e.target.value })} placeholder="email@client.com" /></div>
              </div>

              {createType === "contract" ? (
                <>
                  <div><Label className="text-xs">Contract Title</Label><Input value={form.title || ""} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Service Agreement" /></div>
                  <div><Label className="text-xs">Service Type</Label><Input value={form.service_type || ""} onChange={e => setForm({ ...form, service_type: e.target.value })} placeholder="Management Consulting" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Total Value ($)</Label><Input type="number" value={form.total_value || ""} onChange={e => setForm({ ...form, total_value: parseFloat(e.target.value) || 0 })} /></div>
                    <div><Label className="text-xs">Start Date</Label><Input type="date" value={form.start_date || ""} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
                  </div>
                  <div><Label className="text-xs">Terms</Label><textarea className="w-full border rounded-md p-2 text-sm min-h-[80px]" value={form.terms || ""} onChange={e => setForm({ ...form, terms: e.target.value })} placeholder="Contract terms..." /></div>
                </>
              ) : (
                <>
                  {/* Line Items */}
                  <div>
                    <Label className="text-xs mb-2 block">Line Items</Label>
                    {lineItems.map((item, idx) => (
                      <div key={idx} className="flex gap-2 mb-2">
                        <Input className="flex-1" placeholder="Description" value={item.description} onChange={e => { const updated = [...lineItems]; updated[idx].description = e.target.value; setLineItems(updated); }} />
                        <Input className="w-16" type="number" placeholder="Qty" value={item.quantity} onChange={e => { const updated = [...lineItems]; updated[idx].quantity = parseInt(e.target.value) || 0; setLineItems(updated); }} />
                        <Input className="w-24" type="number" placeholder="Rate" value={item.rate} onChange={e => { const updated = [...lineItems]; updated[idx].rate = parseFloat(e.target.value) || 0; setLineItems(updated); }} />
                        {lineItems.length > 1 && (
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setLineItems(lineItems.filter((_, i) => i !== idx))}><Trash2 className="w-3.5 h-3.5" /></Button>
                        )}
                      </div>
                    ))}
                    <Button size="sm" variant="ghost" onClick={() => setLineItems([...lineItems, { description: "", quantity: 1, rate: 0 }])} className="text-[#2BC40E]">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
                    </Button>
                    <p className="text-right text-sm font-bold mt-2">Total: ${itemTotal.toLocaleString()}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Tax Rate (%)</Label><Input type="number" value={form.tax_rate || 0} onChange={e => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })} /></div>
                    <div><Label className="text-xs">{createType === "estimate" ? "Valid Until" : "Due Date"}</Label><Input type="date" value={form.due_date || form.valid_until || ""} onChange={e => setForm({ ...form, [createType === "estimate" ? "valid_until" : "due_date"]: e.target.value })} /></div>
                  </div>
                  <div><Label className="text-xs">Notes</Label><Input value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." /></div>
                </>
              )}

              <Button onClick={handleSave} disabled={saving} className="w-full bg-[#333333] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Create {createType}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default BillingDashboard;
